import { TaskLocation } from '@/api';
import { useEffect, useState } from 'react';
import { MAPBOX_TOKEN } from '@/config';
import { preprocessCoord } from '../util/preprocess-coord';

type PlacesResponse = {
    features: Array<{
        place_name: string;
    }>;
};

/**
 * The street address a task location falls at, read straight from Mapbox
 * rather than from our own API — the only read in this module that does not go
 * through the generated client.
 *
 * The coordinates are rounded before the lookup, so a device drifting by a
 * metre does not send us round again.
 */
export function useGeocode(location: TaskLocation): {
    status: 'loading' | 'success' | 'error';
    data: string | null;
} {
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
        'success'
    );
    const [data, setData] = useState<string | null>(null);

    const [latitude, setLatitude] = useState(preprocessCoord(location.lat));
    const [longitude, setLongitude] = useState(preprocessCoord(location.lng));

    useEffect(() => {
        setLatitude(preprocessCoord(location.lat));
        setLongitude(preprocessCoord(location.lng));
    }, [location]);

    useEffect(() => {
        if (!latitude || !longitude) {
            setData(null);
            setStatus('success');
            return;
        }

        const lng = longitude;
        const lat = latitude;

        function loadAddress() {
            setStatus('loading');
            fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`
            )
                .then((response) => response.json())
                .then((data: PlacesResponse) => {
                    if (data.features.length > 0) {
                        setData(data.features[0].place_name);
                    }
                    setStatus('success');
                })
                .catch(() => {
                    setStatus('error');
                });
        }

        loadAddress();
    }, [latitude, longitude]);

    return { status, data };
}
